import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../../context/AuthContext";

import {
  getBillingInfo,
  updateBillingInfo,
} from "../../services/billingService";

/* ===================================================== */
/* CONSTANTS                                             */
/* ===================================================== */

const EMPTY_FORM = {
  legalName: "",
  billingEmail: "",
  gstin: "",
  pan: "",
  address: {
    line1: "",
    line2: "",
    district: "",
    state: "",
    pincode: "",
    country: "",
  },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COUNTRY_CODE_REGEX = /^[A-Z]{2}$/;

const MAX_LEGAL_NAME_LENGTH = 150;
const MAX_COUNTRY_LENGTH = 100;

/* ===================================================== */
/* HELPERS                                               */
/* ===================================================== */

const normalizeText = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
};

const normalizeCountry = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  const country = value.trim().toUpperCase();

  if (!country) {
    return "";
  }

  // Normalize common India representations.
  if (country === "IND" || country === "INDIA") {
    return "IN";
  }

  return country;
};

const normalizeEmail = (value) => {
  return normalizeText(value);
};

const cloneForm = (form) => {
  if (!form) {
    return null;
  }

  return {
    ...form,
    address: {
      ...EMPTY_FORM.address,
      ...(form.address || {}),
    },
  };
};

/**
 * Extract a company object from supported response structures.
 *
 * Supports:
 *   { company: {...} }
 *   { data: { company: {...} } }
 *   {...company fields}
 *
 * billingService.js normally returns the company object directly,
 * but this helper also supports wrapped responses.
 */
const extractCompany = (response) => {
  if (
    !response ||
    typeof response !== "object" ||
    Array.isArray(response)
  ) {
    return null;
  }

  if (
    response.company &&
    typeof response.company === "object" &&
    !Array.isArray(response.company)
  ) {
    return response.company;
  }

  if (
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    if (
      response.data.company &&
      typeof response.data.company === "object" &&
      !Array.isArray(response.data.company)
    ) {
      return response.data.company;
    }

    return response.data;
  }

  return response;
};

/**
 * Normalize server billing data for the form.
 *
 * Country is taken from the saved company address only.
 * It is intentionally not replaced with the active company's
 * country when the saved billing country is missing.
 */
const normalizeBillingInfo = (company, userEmail = "") => {
  const source =
    company && typeof company === "object"
      ? company
      : {};

  const billingAddress =
    source.address &&
    typeof source.address === "object" &&
    !Array.isArray(source.address)
      ? source.address
      : {};

  const billingEmail =
    source.contact?.billingEmail ||
    source.contact?.financeEmail ||
    source.contact?.email ||
    source.billingEmail ||
    source.financeEmail ||
    userEmail ||
    "";

  return {
    legalName: normalizeText(
      source.legalName || source.name || ""
    ),

    billingEmail: normalizeEmail(billingEmail),

    gstin: normalizeText(source.gstin),
    pan: normalizeText(source.pan),

    address: {
      line1: normalizeText(billingAddress.line1),
      line2: normalizeText(billingAddress.line2),
      district: normalizeText(billingAddress.district),
      state: normalizeText(billingAddress.state),
      pincode: normalizeText(billingAddress.pincode),
      country: normalizeCountry(billingAddress.country),
    },
  };
};

const validateForm = (form) => {
  if (!form) {
    return "Billing information is unavailable.";
  }

  if (
    typeof form.legalName !== "string" ||
    typeof form.billingEmail !== "string" ||
    !form.address ||
    typeof form.address !== "object"
  ) {
    return "Billing information is invalid. Please reload the page.";
  }

  if (
    form.legalName.trim().length >
    MAX_LEGAL_NAME_LENGTH
  ) {
    return `Legal company name cannot exceed ${MAX_LEGAL_NAME_LENGTH} characters.`;
  }

  if (
    form.billingEmail &&
    !EMAIL_REGEX.test(form.billingEmail.trim())
  ) {
    return "Please enter a valid billing email address.";
  }

  const country = normalizeCountry(
    form.address.country
  );

  if (
    country &&
    !COUNTRY_CODE_REGEX.test(country)
  ) {
    return "Enter a valid two-letter country code, such as GU or IN.";
  }

  if (
    typeof form.address.country === "string" &&
    form.address.country.length > MAX_COUNTRY_LENGTH
  ) {
    return `Country cannot exceed ${MAX_COUNTRY_LENGTH} characters.`;
  }

  return "";
};

const getErrorMessage = (error, fallback) => {
  return (
    error?.response?.data?.message ||
    error?.raw?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

const hasOwn = (object, property) => {
  return Object.prototype.hasOwnProperty.call(
    object,
    property
  );
};

/* ===================================================== */
/* COMPONENT                                             */
/* ===================================================== */

export default function BillingInfoCard() {
  const { user, activeCompany } = useAuth();
  const userEmail = user?.email || "";
  const companyId = activeCompany?._id
    ? String(activeCompany._id)
    : "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isEditing, setIsEditing] = useState(false);

  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);

  /* ===================================================== */
  /* LOAD                                                  */
  /* ===================================================== */

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      setIsEditing(false);

      if (!companyId) {
        setForm(null);
        setOriginal(null);
        setLoading(false);
        return;
      }

      setForm(null);
      setOriginal(null);

      try {
        const response = await getBillingInfo(companyId);

        if (!active) {
          return;
        }

        const company = extractCompany(response);

        if (!company) {
          throw new Error(
            "The server did not return valid company billing information."
          );
        }

        // Avoid displaying a response for another company.
        const responseCompanyId =
          company._id != null
            ? String(company._id)
            : company.id != null
              ? String(company.id)
              : "";

        if (
          responseCompanyId &&
          responseCompanyId !== companyId
        ) {
          throw new Error(
            "The server returned billing information for a different company."
          );
        }

        const mapped = normalizeBillingInfo(
          company,
          userEmail
        );

        setForm(mapped);
        setOriginal(cloneForm(mapped));
      } catch (err) {
        if (!active) {
          return;
        }

        console.error(
          "[BillingInfoCard] Failed to load billing information:",
          err
        );

        setForm(null);
        setOriginal(null);

        setError(
          getErrorMessage(
            err,
            "Failed to load billing information. Please try again."
          )
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [companyId, userEmail]);
  /* ===================================================== */
  /* DERIVED STATE                                         */
  /* ===================================================== */

  const isDirty = useMemo(() => {
    if (!form || !original) {
      return false;
    }

    return JSON.stringify(form) !== JSON.stringify(original);
  }, [form, original]);

  const formattedAddress = useMemo(() => {
    if (!form) {
      return "—";
    }
     
    const addressParts = [
      form.address.line1,
      form.address.line2,
      form.address.district,
      form.address.state,
      form.address.pincode,
      form.address.country,
    ]
      .map(normalizeText)
      .filter(Boolean);

    return addressParts.join(", ") || "—";
  }, [form]);

  /* ===================================================== */
  /* FIELD UPDATES                                         */
  /* ===================================================== */

  const updateField = useCallback((path, value) => {
    setForm((previous) => {
      if (!previous) {
        return previous;
      }

      const keys = path.split(".");

      if (keys.length === 1) {
        return {
          ...previous,
          [keys[0]]: value,
        };
      }

      if (
        keys.length === 2 &&
        keys[0] === "address" &&
        hasOwn(EMPTY_FORM.address, keys[1])
      ) {
        return {
          ...previous,
          address: {
            ...previous.address,
            [keys[1]]: value,
          },
        };
      }

      return previous;
    });

    setError("");
    setSuccess("");
  }, []);

  /* ===================================================== */
  /* SAVE                                                  */
  /* ===================================================== */

  const handleSave = async (event) => {
    event?.preventDefault();

    if (saving || !companyId || !form) {
      return;
    }

    const validationError = validateForm(form);

    if (validationError) {
      setError(validationError);
      setSuccess("");
      return;
    }

    if (!isDirty) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    const requestedCompanyId = companyId;

    try {
      const payload = {
        legalName: normalizeText(form.legalName),
        billingEmail: normalizeEmail(form.billingEmail),
        gstin: normalizeText(form.gstin),
        pan: normalizeText(form.pan),

        address: {
          line1: normalizeText(form.address.line1),
          line2: normalizeText(form.address.line2),
          district: normalizeText(form.address.district),
          state: normalizeText(form.address.state),
          pincode: normalizeText(form.address.pincode),
          country: normalizeCountry(form.address.country),
        },
      };

      const response = await updateBillingInfo(
        requestedCompanyId,
        payload
      );

      /*
       * If the user switched companies while the request was in
       * progress, do not apply the old company's result to the new
       * company's form.
       */
      if (requestedCompanyId !== companyId) {
        return;
      }

      const savedCompany = extractCompany(response);

      let savedForm;

      if (savedCompany) {
        const responseCompanyId =
          savedCompany._id != null
            ? String(savedCompany._id)
            : savedCompany.id != null
              ? String(savedCompany.id)
              : "";

        if (
          responseCompanyId &&
          responseCompanyId !== requestedCompanyId
        ) {
          throw new Error(
            "The server returned billing information for a different company."
          );
        }

        savedForm = normalizeBillingInfo(
          savedCompany,
          userEmail
        );
      } else {
        /*
         * The endpoint may return success metadata without the
         * updated company. In that case, use the submitted values.
         *
         * The server remains the source of truth on the next reload.
         */
        savedForm = normalizeBillingInfo(
        {
          ...payload,
          name: payload.legalName,
        },
        userEmail
      );
      }

      setForm(savedForm);
      setOriginal(cloneForm(savedForm));
      setIsEditing(false);

      setSuccess(
        "Billing information updated successfully."
      );
    } catch (err) {
      console.error(
        "[BillingInfoCard] Failed to save billing information:",
        err
      );

      setError(
        getErrorMessage(
          err,
          "Failed to update billing information. Please try again."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /* ===================================================== */
  /* CANCEL                                                */
  /* ===================================================== */

  const handleCancel = () => {
    setForm(cloneForm(original));
    setIsEditing(false);
    setError("");
    setSuccess("");
  };

  /* ===================================================== */
  /* LOADING STATES                                        */
  /* ===================================================== */

  if (loading) {
    return (
      <div
        className="rounded-2xl border bg-white p-6 text-sm text-gray-500"
        role="status"
        aria-live="polite"
      >
        Loading billing information…
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="rounded-2xl border bg-white p-6 text-sm text-gray-500">
        Select a company to view billing information.
      </div>
    );
  }

  if (!form) {
    return (
      <div className="space-y-3 rounded-2xl border bg-white p-6">
        <p className="text-sm text-gray-500">
          Billing information could not be loaded.
        </p>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={() => {
            // Changing a state value is required to trigger a retry.
            setError("");
            setLoading(true);
            setForm(null);
            setOriginal(null);

            getBillingInfo(companyId)
              .then((response) => {
                const company = extractCompany(response);

                if (!company) {
                  throw new Error(
                    "Invalid company billing response."
                  );
                }

                const mapped = normalizeBillingInfo(
                  company,
                  userEmail
                );

                setForm(mapped);
                setOriginal(cloneForm(mapped));
              })
              .catch((err) => {
                setError(
                  getErrorMessage(
                    err,
                    "Failed to load billing information. Please try again."
                  )
                );
              })
              .finally(() => {
                setLoading(false);
              });
          }}
          className="rounded border px-3 py-2 text-sm hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

  return (
    <section
      className="space-y-5 rounded-2xl border bg-white p-6"
      aria-labelledby="billing-information-heading"
      aria-busy={saving}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2
            id="billing-information-heading"
            className="text-lg font-semibold"
          >
            Billing Information
          </h2>

          <p className="text-sm text-gray-500">
            This information appears on invoices.
          </p>
        </div>

        {!isEditing && (
          <button
            type="button"
            onClick={() => {
              setError("");
              setSuccess("");
              setIsEditing(true);
            }}
            className="rounded border px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Edit
          </button>
        )}
      </div>

      {/* Feedback */}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          role="status"
          aria-live="polite"
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700"
        >
          {success}
        </div>
      )}

      {/* VIEW MODE */}
      {!isEditing && (
        <div className="space-y-3 text-sm">
          <Info
            label="Legal Name"
            value={form.legalName || "—"}
          />

          <Info
            label="Billing Email"
            value={form.billingEmail || "—"}
          />

          <Info
            label="Address"
            value={formattedAddress}
          />

          <Info
            label="GST Number"
            value={form.gstin || "—"}
          />

          <Info
            label="PAN Number"
            value={form.pan || "—"}
          />
        </div>
      )}

      {/* EDIT MODE */}
      {isEditing && (
        <form
          className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2"
          onSubmit={handleSave}
          noValidate
        >
          <Field
            label="Legal Company Name"
            value={form.legalName}
            onChange={(value) =>
              updateField("legalName", value)
            }
            maxLength={MAX_LEGAL_NAME_LENGTH}
            autoComplete="organization"
          />

          <Field
            label="Billing Email"
            type="email"
            value={form.billingEmail}
            onChange={(value) =>
              updateField("billingEmail", value)
            }
            autoComplete="email"
          />

          <Field
            label="Address Line 1"
            value={form.address.line1}
            onChange={(value) =>
              updateField("address.line1", value)
            }
            autoComplete="address-line1"
          />

          <Field
            label="Address Line 2"
            value={form.address.line2}
            onChange={(value) =>
              updateField("address.line2", value)
            }
            autoComplete="address-line2"
          />

          <Field
            label="District"
            value={form.address.district}
            onChange={(value) =>
              updateField("address.district", value)
            }
            autoComplete="address-level2"
          />

          <Field
            label="State / Province"
            value={form.address.state}
            onChange={(value) =>
              updateField("address.state", value)
            }
            autoComplete="address-level1"
          />

          <Field
            label="Postal / ZIP Code"
            value={form.address.pincode}
            onChange={(value) =>
              updateField("address.pincode", value)
            }
            autoComplete="postal-code"
          />

          <Field
            label="Country Code"
            value={form.address.country}
            onChange={(value) =>
              updateField(
                "address.country",
                value.toUpperCase()
              )
            }
            placeholder="e.g. GU"
            maxLength={2}
            autoComplete="country"
            aria-describedby="billing-country-help"
          />

          <p
            id="billing-country-help"
            className="text-xs text-gray-500 md:col-span-2"
          >
            Enter the two-letter ISO country code, such as GU
            for Guam or IN for India.
          </p>

          <Field
            label="GST Number (optional)"
            value={form.gstin}
            onChange={(value) =>
              updateField("gstin", value)
            }
          />

          <Field
            label="PAN Number (optional)"
            value={form.pan}
            onChange={(value) =>
              updateField("pan", value)
            }
          />

          <div className="col-span-full flex flex-wrap justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="rounded border px-4 py-2 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving || !isDirty}
              className="rounded bg-slate-900 px-4 py-2 text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

/* ===================================================== */
/* UI HELPERS                                            */
/* ===================================================== */

function Info({ label, value }) {
  return (
    <div>
      <div className="text-gray-500">
        {label}
      </div>

      <div className="break-words font-medium text-gray-900">
        {value}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  maxLength,
  autoComplete,
  ...inputProps
}) {
  const id = React.useId();

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-gray-600"
      >
        {label}
      </label>

      <input
        {...inputProps}
        id={id}
        type={type}
        value={value ?? ""}
        onChange={(event) =>
          onChange(event.target.value)
        }
        placeholder={placeholder}
        maxLength={maxLength}
        autoComplete={autoComplete}
        className="w-full rounded border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-200"
      />
    </div>
  );
}